import * as React from 'react';
// mui
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Container,
  FormControlLabel,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
  Box,
  AppBar
} from '@mui/material';
// router
import { useNavigate } from 'react-router';
// redux
import { useAppDispatch } from '../../store/hooks';
import { setToken, setTimezone } from '../../store/slices/authSlice';
// axios
import api from '../../axios';

interface AuthDto {
  email: string;
  password: string;
  key?: string; // optionally included for register form
}

export default function AuthCard() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [tab, setTab] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [showSnackbar, setShowSnackbar] = React.useState(false);
  const [showEmailConfirmationNotice, setShowEmailConfirmationNotice] = React.useState(false);

  // form state
  const [email, setEmail] = React.useState('');
  const [emailError, setEmailError] = React.useState<string>('');

  const [password, setPassword] = React.useState('');
  const [passwordError, setPasswordError] = React.useState<string>('');

  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [confirmPasswordError, setConfirmPasswordError] = React.useState<string>('');

  const [TOSCheck, setTOSCheck] = React.useState(false);
  const [key, setKey] = React.useState('');

  const submitForm = (e: any) => {
    e.preventDefault();
    setIsLoading(true);

    let validationFail: boolean = false;

    if (!email.includes('@') && !email.includes('.') && email.length < 6) {
      setEmailError('Must be a valid email!');
      setIsLoading(false);
      validationFail = true;
    } else {
      setEmailError('');
    }

    if (password.length < 4) {
      setPasswordError('Password must be longer than 4 characters!');
      setIsLoading(false);
      validationFail = true;
    } else {
      setPasswordError('');
    }

    if (tab === 1 && confirmPassword !== password) {
      setConfirmPasswordError('Passwords must match!');
      setIsLoading(false);
      validationFail = true;
    } else {
      setConfirmPasswordError('');
    }

    if (validationFail) { return; } // dont post if validation fails

    const postData: AuthDto = {
      email,
      password
    };

    if (tab === 1) { // register
      postData.key = key;
    }

    api.post(`auth/${tab === 0 ? 'login' : 'register'}`, postData)
      .then((res) => {
        const { data } = res;
        if (tab === 0) { // login
          localStorage.setItem('jwtToken', data.access_token);
          localStorage.setItem('refreshToken', data.refresh_token);
          localStorage.setItem('timezone', data.timezone);
          dispatch(setToken(data.access_token));
          dispatch(setTimezone(data.timezone));
          navigate('/app/');
        } else { // register
          setEmail('');
          setPassword('');
          setKey('');
          setShowEmailConfirmationNotice(true);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message === 'Email already registered!') {
          setEmailError('Email already in use!');
        } if (err.response?.data?.message === 'User Email Not Confirmed!') {
          setEmail('');
          setPassword('');
          setKey('');
          setShowEmailConfirmationNotice(true);
        } else {
          setShowSnackbar(true);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  return (
    <Card sx={{ width: 500 }}>
      <AppBar position="static">
        <Tabs
          value={tab}
          onChange={(e, val) => { setTab(val); }}
          indicatorColor="primary"
          textColor="inherit"
          variant="fullWidth"
          aria-label="full width tabs example"
        >
          <Tab
            disabled={showEmailConfirmationNotice}
            label="Login"
          />
          <Tab
            disabled={showEmailConfirmationNotice}
            label="Signup"
          />
        </Tabs>
      </AppBar>

      <Container sx={{ marginY: 3 }}>
        { showEmailConfirmationNotice ?
          <React.Fragment>
            <Typography variant="h6">Thank You for Signing Up!</Typography>
            <Typography>Please check your inbox to verify your email.</Typography>
          </React.Fragment>
        :
          <Box
            component="form"
            noValidate
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 2,
            }}
            onSubmit={submitForm}
            autoComplete="off"
          >
            <TextField
              value={email}
              onChange={(e) => { setEmail(e.target.value); }}
              required
              fullWidth
              error={!!emailError}
              helperText={emailError}
              id="outlined-required"
              label="Email"
            />
            <TextField
              value={password}
              onChange={(e) => { setPassword(e.target.value); }}
              required
              fullWidth
              error={!!passwordError}
              helperText={passwordError}
              id="outlined-password-input"
              label="Password"
              type="password"
            />
            {tab === 1 && (
              <React.Fragment>
                <TextField
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); }}
                  required
                  fullWidth
                  error={!!confirmPasswordError}
                  helperText={confirmPasswordError}
                  id="outlined-password-input"
                  label="Confirm Password"
                  type="password"
                />
                <TextField
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  required
                  fullWidth
                  label="Alpha Tester Key"
                />
                <FormControlLabel value={TOSCheck} onChange={(e, checked) => setTOSCheck(checked)} required control={<Checkbox />} label="I agree to the terms of service" />
              </React.Fragment>
            )}
            <Button
              type="submit"
              disabled={tab === 0 ? (!email || !password) : (!email || !password || !key || !TOSCheck)}
              fullWidth
              loading={isLoading}
              onClick={submitForm}
              variant="contained"
            >{tab === 0 ? 'Login' : 'Signup'}</Button>
          </Box>
        }
        
        <Snackbar
          open={showSnackbar}
          autoHideDuration={6000}
          onClose={() => { setShowSnackbar(false); }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center'
          }}
        >
          <Alert
            onClose={() => { setShowSnackbar(false); }}
            severity="error"
            variant="filled"
            sx={{ width: '100%' }}
          >
            Invalid Credentials!
          </Alert>
        </Snackbar>
      </Container>
    </Card>
  );
}